import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import classnames from 'clsx';
import { BehaviorSubject, of } from 'rxjs';
import { filter, debounceTime, switchMap, catchError, map } from 'rxjs/operators';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';

import { ResultList } from './result-list';
import style from './style.module.less';
import utilityStyle from '../../css-variables/utilities.module.css';
import { getProductSearchResults } from '../../store/products/products.api';

/**
 * ProductAutoComplete Component
 * @param {object} props - The component props.
 * @param {string} props.ariaLabel - The ARIA label for accessibility.
 * @param {string} [props.className] - Optional additional CSS classes.
 * @param {(product: object) => void} props.onSelectionMade - Callback when a selection is made, receives the selected product as an argument.
 * @param {string} [props.selectedProductText] - The text of the selected product.
 * @param {() => void} [props.onInputCleared] - Callback when input is cleared.
 * @param {string} [props.dropdownClassName] - Optional CSS classes for the dropdown.
 * @param {string} props.searchTerm - The current search term in the input.
 * @param {(term: string) => void} props.setSearchTerm - Function to set the search term, receives the new search term as an argument.
 * @param {boolean} [props.isMultiSelect] - Allows multiple selections if true.
 * @param {string} [props.placeholder] - Placeholder text for the input.
 * @param {string} [props.id] - Id for the input.
 * @param {boolean} [props.keepInputEnabled] - Prevents the input from being disabled after selection is made.
 * @returns {JSX.Element} The ProductAutoComplete component.
 */
export const ProductAutoComplete = ({
  ariaLabel,
  className = '',
  onSelectionMade,
  selectedProductText = '',
  onInputCleared = noop,
  dropdownClassName = '',
  searchTerm,
  setSearchTerm,
  isMultiSelect,
  placeholder = '',
  id,
  keepInputEnabled,
}) => {
  const behaviorSubject = useRef(new BehaviorSubject(''));
  /** @type {React.RefObject<HTMLInputElement>} */
  const inputRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);
  const [selectionWasMade, setSelectionWasMade] = useState(false);
  const minSearchTermLength = 3;

  const onChange = useCallback(
    (e) => {
      behaviorSubject?.current.next(e.target.value);
      setSearchTerm(e.target.value);
    },
    [setSearchTerm],
  );
  const selectionMade = useCallback(
    (product) => {
      behaviorSubject?.current.next('');
      setSearchTerm('');
      setSelectionWasMade(true);
      setSearchResults([]);
      onSelectionMade(product);
      setShouldFocusInput(true);
    },
    [setSearchTerm, setSelectionWasMade, setSearchResults, onSelectionMade, behaviorSubject],
  );
  const clearInput = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectionWasMade(false);
    onInputCleared();
  }, [setSearchTerm, setSearchResults, setSelectionWasMade, onInputCleared]);

  useEffect(() => {
    const currentBehaviorSubject = behaviorSubject.current;
    const searchResult$ = currentBehaviorSubject.pipe(
      filter((value) => value.length >= minSearchTermLength),
      debounceTime(500),
      switchMap((value) => {
        return getProductSearchResults(value).pipe(
          map((results) => results),
          catchError(() => of([])),
        );
      }),
    );
    const subscription = searchResult$.subscribe((results) => {
      setSearchResults(results);
    });
    setSelectionWasMade(selectedProductText?.length > 0);
    return () => {
      subscription.unsubscribe();
      currentBehaviorSubject.next('');
    };
  }, [behaviorSubject, selectedProductText, setSelectionWasMade]);

  useEffect(() => {
    if (shouldFocusInput && inputRef.current) {
      inputRef.current.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput]);

  const inputDisabled = !keepInputEnabled && selectionWasMade && !isMultiSelect;
  const isMinSearchTermLength = searchTerm.length >= minSearchTermLength;
  const inputValue = selectedProductText || searchTerm;

  return (
    <div aria-label={ariaLabel} className={classnames(style['product-auto-complete'], className)}>
      {searchResults.length > 0 && isMinSearchTermLength ? (
        <ResultList
          dropdownClassName={dropdownClassName}
          onSelectionMade={selectionMade}
          searchResults={searchResults}
          searchTerm={searchTerm}
        />
      ) : null}
      <input
        ref={inputRef}
        autoComplete='off'
        className={classnames(utilityStyle['input-border'], style['product-input'])}
        disabled={inputDisabled}
        id={id}
        onChange={onChange}
        placeholder={placeholder}
        value={inputValue}
      />
      {inputValue ? (
        <Button className={style['clear-input']} onClick={clearInput} theme='unstyled'>
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      ) : (
        <Button className={style['search-icon']} theme='unstyled'>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </Button>
      )}
    </div>
  );
};
